import React, { createContext, useState, useContext, useEffect, useCallback, useMemo, ReactNode, ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConfirmOptions, useConfirm } from './ConfirmContext';
import { aiLeavePrompt, useAiJobs } from '../hooks/useAiWork';

type LeavePrompt = ConfirmOptions & { message: string };

const UNSAVED_PROMPT: LeavePrompt = {
    danger: true,
    entity: "Unsaved Changes",
    titleVerb: "Discard",
    confirmLabel: "Leave",
    cancelLabel: "Stay",
    message: 'You have unsaved changes. Are you sure you want to leave?',
};

// Define the shape of the context
interface DirtyContextType {
    isDirty: boolean;
    setIsDirty: React.Dispatch<React.SetStateAction<boolean>>;

    // --- Per-tab dirty registry (additive) ---------------------------------
    // The mission page (and any other multi-tab page) can track dirtiness per
    // tab without disturbing the page-global isDirty/setIsDirty used elsewhere.
    // A tab is keyed by an arbitrary stable string (e.g. "info", "parties").
    /** Map of tabKey -> dirty flag. Only keys explicitly set to true are dirty. */
    tabDirty: Record<string, boolean>;
    /** Set (or clear) the dirty flag for a single tab. */
    setTabDirty: (tabKey: string, dirty: boolean) => void;
    /** True when the given tab is currently marked dirty. */
    isTabDirty: (tabKey: string) => boolean;
    /** True when ANY registered tab is currently dirty. */
    anyTabDirty: () => boolean;
    /** Remove a tab from the registry entirely (e.g. on unmount / reset). */
    clearTabDirty: (tabKey: string) => void;
    /** Clear the entire per-tab registry (e.g. when leaving the page). */
    clearAllTabDirty: () => void;
}

// Create the context with a default value of null
const DirtyContext = createContext<DirtyContextType | null>(null);

// Define the props for the provider component
interface DirtyProviderProps {
    children: ReactNode;
}

// Create the provider component
export const DirtyProvider = ({ children }: DirtyProviderProps): ReactElement => {
    const [isDirty, setIsDirty] = useState<boolean>(false);

    // Per-tab dirty registry. Kept independent from the page-global isDirty so
    // existing single-form consumers (Company/Client/Lead/Merge/useCustomNavigate)
    // are completely unaffected — they continue to read/write isDirty directly.
    const [tabDirty, setTabDirtyState] = useState<Record<string, boolean>>({});

    const setTabDirty = useCallback((tabKey: string, dirty: boolean) => {
        setTabDirtyState((prev) => {
            // No-op when the value is unchanged so we don't churn renders.
            if (!!prev[tabKey] === dirty) {
                return prev;
            }
            return { ...prev, [tabKey]: dirty };
        });
    }, []);

    const isTabDirty = useCallback((tabKey: string) => !!tabDirty[tabKey], [tabDirty]);

    const anyTabDirty = useCallback(() => Object.values(tabDirty).some(Boolean), [tabDirty]);

    const clearTabDirty = useCallback((tabKey: string) => {
        setTabDirtyState((prev) => {
            if (!(tabKey in prev)) {
                return prev;
            }
            const next = { ...prev };
            delete next[tabKey];
            return next;
        });
    }, []);

    const clearAllTabDirty = useCallback(() => {
        setTabDirtyState((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    }, []);

    // The page-level prompt fires when the global isDirty is set OR any tab is
    // dirty. This preserves existing behavior for non-tab consumers while
    // letting the per-tab registry participate in the leave guard.
    const hasUnsavedChanges = isDirty || Object.values(tabDirty).some(Boolean);

    // Leaving while an AI button works asks too: the result would land on a page nobody sees.
    const aiLabel = useAiJobs()[0]?.label;
    const leavePrompt = useMemo(
        () => (aiLabel !== undefined ? aiLeavePrompt(aiLabel, hasUnsavedChanges) : UNSAVED_PROMPT),
        [aiLabel, hasUnsavedChanges],
    );

    // Custom hook to handle navigation prompts
    usePrompt(leavePrompt, hasUnsavedChanges || aiLabel !== undefined, setIsDirty, clearAllTabDirty);

    return (
        <DirtyContext.Provider value={{
            isDirty,
            setIsDirty,
            tabDirty,
            setTabDirty,
            isTabDirty,
            anyTabDirty,
            clearTabDirty,
            clearAllTabDirty,
        }}>
            {children}
        </DirtyContext.Provider>
    );
};

// Custom hook to handle the navigation prompt
const usePrompt = (
    prompt: LeavePrompt,
    when: boolean,
    setIsDirty: React.Dispatch<React.SetStateAction<boolean>>,
    clearAllTabDirty: () => void,
) => {
    const navigate = useNavigate();
    const location = useLocation();
    // Themed confirm (mobile browsers suppress window.confirm, which used to
    // silently block back/forward navigation away from dirty forms on phones).
    const confirm = useConfirm();

    useEffect(() => {
        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (when) {
                // Tab close / hard reload: browsers only honor the native
                // (synchronous) beforeunload prompt here — an in-app modal
                // can't intercept it, so this stays native by design.
                event.preventDefault();
                event.returnValue = prompt.message; // For most browsers
            }
        };

        const handleNavigation = async (event: PopStateEvent) => {
            if (when) {
                const confirmLeave = await confirm(prompt);
                if (!confirmLeave) {
                    // If the user cancels, stay on the current route
                    event.preventDefault();
                    navigate(location.pathname); // Navigate back to the current location
                } else {
                    setIsDirty(false); // Reset dirty state if they confirm leaving
                    clearAllTabDirty(); // Also clear any per-tab dirty flags
                }
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('popstate', handleNavigation); // Handle browser navigation (back/forward)


        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('popstate', handleNavigation);
        };
    }, [when, prompt, navigate, location.pathname, setIsDirty, clearAllTabDirty, confirm]);

};

// Custom hook to use the DirtyContext
export const useDirtyContext = (): DirtyContextType => {
    const context = useContext(DirtyContext);
    if (!context) {
        throw new Error('useDirtyContext must be used within a DirtyProvider');
    }
    return context;
};
